/**
 * Read / write / parse CSS custom properties. Centralizes the bridge between the
 * design-system's CSS-token source of truth and any JS that needs to consume or
 * mutate a token at runtime — Floating UI middleware derivables, Ladle theme
 * controls, future imperative cases.
 *
 * Design rule: prefer reading these helpers over hardcoding numeric mirrors of
 * CSS tokens. A change to a token in CSS should propagate without parallel JS
 * updates.
 */

/** Supported CSS length units. Intentionally narrow: only the units our design
 *  tokens actually use today. Adding a new unit means deciding how it resolves to
 *  pixels (font-size / viewport / container context) and updating the switch below. */
type SupportedLengthUnit = 'px' | 'rem' | ''

const isSupportedUnit = (unit: string): unit is SupportedLengthUnit =>
  unit === 'px' || unit === 'rem' || unit === ''

const LENGTH_PATTERN = /^(-?\d*\.?\d+)([a-z%]*)$/i

/** Read a custom property's resolved cascaded value from `target` (defaults to <html>).
 *  Returns the value verbatim — e.g. "0.25rem", "4px", or "" if undefined. */
export const getCssToken = (
  token: `--${string}`,
  target: HTMLElement = document.documentElement,
): string => getComputedStyle(target).getPropertyValue(token).trim()

/** Write a custom property to `target` (defaults to <html>). */
export const setCssToken = (
  token: `--${string}`,
  value: string,
  target: HTMLElement = document.documentElement,
): void => {
  target.style.setProperty(token, value)
}

/** Clear a custom property from `target`'s inline style (defaults to <html>). The
 *  cascaded value falls back to whatever the stylesheet defines on `:root`. */
export const removeCssToken = (
  token: `--${string}`,
  target: HTMLElement = document.documentElement,
): void => {
  target.style.removeProperty(token)
}

/** Parse a CSS length string ("0.25rem" / "4px" / "8") into a pixel number.
 *  rem is resolved against <html>'s computed font-size, which is where
 *  --theme-density lives — so the resulting px tracks density. Throws if the
 *  string has an unrecognized shape or a unit outside the supported set, so a
 *  typo or accidental unit change in a token fails loudly rather than silently
 *  producing 0 or NaN. */
export const parseCssLengthPx = (value: string): number => {
  const trimmed = value.trim()
  if (trimmed === '') {
    return 0
  }
  const match = LENGTH_PATTERN.exec(trimmed)
  if (!match) {
    throw new Error(`parseCssLengthPx: unrecognized length "${value}"`)
  }
  const numeric = parseFloat(match[1] ?? '')
  const unit = (match[2] ?? '').toLowerCase()
  if (!isSupportedUnit(unit)) {
    throw new Error(
      `parseCssLengthPx: unsupported unit "${unit}" in "${value}". Supported: px, rem.`,
    )
  }
  switch (unit) {
    case '':
    case 'px':
      return numeric
    case 'rem':
      return numeric * parseFloat(getComputedStyle(document.documentElement).fontSize)
  }
}
